/**
 * A route carried in a URL, from the price checker into the order form.
 *
 * ## Why the URL and not storage
 *
 * The shop reading a price is doing it inside Messenger's in-app browser. Pressing *Ituloy*
 * sends them to sign in, and Google **refuses OAuth inside an embedded webview**
 * (`services/auth.js`), so the journey has to finish in Chrome. Nothing in `localStorage`
 * or `sessionStorage` crosses from one browser to the other. The URL is the only thing that
 * does, so the URL is where the answer travels.
 *
 * `returnTo.js` carries the *destination* across the sign-in itself; this carries the
 * *content* across the browsers. They are different problems and neither replaces the other.
 *
 * ## Everything here is untrusted
 *
 * A URL is typed, shared, edited and mangled by chat clients. Every value is checked on the
 * way in and dropped rather than repaired: a half-parsed pin is worse than none, because it
 * fills the form with an answer the customer did not give and may not read before pressing
 * *Iorder na*.
 *
 * The pins are re-validated by the server at placement regardless — the service-area check
 * and the fare both run there — so nothing here is a security boundary. It is a correctness
 * one.
 */

/** Long enough for a full Nominatim line, short enough that a pasted essay cannot ride in. */
const MAX_LABEL = 160

const KEYS = {
  pickupLat: 'plat',
  pickupLng: 'plng',
  pickupLabel: 'pl',
  pickupName: 'pn',
  dropoffLat: 'dlat',
  dropoffLng: 'dlng',
  dropoffLabel: 'dl',
  riderHint: 'rh',
}

/** A signed hint is a JWT. Long, but nowhere near what a URL will carry. */
const MAX_HINT = 800

const coord = (value) => {
  if (value === null || value === undefined || value === '') return null

  const number = Number(value)

  // `Number(null)` is 0 and 0 is finite — the same trap `places.js` documents. Ruled out
  // above rather than here, so a real zero coordinate would still be honoured.
  return Number.isFinite(number) ? number : null
}

const text = (value) => String(value ?? '').trim().slice(0, MAX_LABEL)

/**
 * The query string for an *Ituloy* link.
 *
 * Only writes what it has. A missing pin leaves its keys out entirely rather than writing
 * empty ones, so the resulting URL says what it means and stays short enough to survive a
 * chat client that decides to shorten it.
 *
 * @returns {URLSearchParams}
 */
export function toPrefillParams({ pickup, pickupSource, dropoff, riderHint } = {}) {
  const params = new URLSearchParams()

  if (coord(pickup?.lat) !== null && coord(pickup?.lng) !== null) {
    params.set(KEYS.pickupLat, String(pickup.lat))
    params.set(KEYS.pickupLng, String(pickup.lng))
    if (text(pickup.label)) params.set(KEYS.pickupLabel, text(pickup.label))
    if (text(pickupSource)) params.set(KEYS.pickupName, text(pickupSource))
  }

  if (coord(dropoff?.lat) !== null && coord(dropoff?.lng) !== null) {
    params.set(KEYS.dropoffLat, String(dropoff.lat))
    params.set(KEYS.dropoffLng, String(dropoff.lng))
    if (text(dropoff.label)) params.set(KEYS.dropoffLabel, text(dropoff.label))
  }

  /**
   * Who the price was measured from, so placement can offer them first.
   *
   * Signed by the server and opaque here — see `backend/src/utils/riderHint.js`. It is not
   * a reservation and carries no money: at worst it names a rider who has since gone
   * offline, and the matcher decides as if it had never been sent.
   */
  const hint = String(riderHint ?? '').trim()

  if (hint && hint.length <= MAX_HINT) params.set(KEYS.riderHint, hint)

  return params
}

/**
 * What the order form should open with, read back out of a query string.
 *
 * Returns `null` for an end that did not arrive whole — **both** coordinates or neither.
 * A latitude with no longitude is not half an answer, it is a pin in the sea.
 *
 * @param {string|URLSearchParams} search
 * @returns {{pickup: object|null, pickupSource: string, dropoff: object|null}}
 */
export function readPrefill(search) {
  const params = typeof search === 'string' ? new URLSearchParams(search) : (search ?? new URLSearchParams())

  const place = (latKey, lngKey, labelKey) => {
    const lat = coord(params.get(latKey))
    const lng = coord(params.get(lngKey))

    if (lat === null || lng === null) return null

    const label = text(params.get(labelKey))

    // `query` mirrors the label, the same as choosing a suggestion does — see
    // `useLocationSearch.selectSuggestion`. The box has to read the same however the
    // answer arrived.
    return { query: label, lat, lng, label }
  }

  const hint = String(params.get(KEYS.riderHint) ?? '').trim()

  return {
    pickup: place(KEYS.pickupLat, KEYS.pickupLng, KEYS.pickupLabel),
    pickupSource: text(params.get(KEYS.pickupName)),
    dropoff: place(KEYS.dropoffLat, KEYS.dropoffLng, KEYS.dropoffLabel),
    // Passed straight back to the server, which is the only thing that can read it. An
    // over-long one is dropped rather than truncated: half a signature verifies as nothing,
    // and sending it would only cost a round trip to be ignored.
    riderHint: hint && hint.length <= MAX_HINT ? hint : '',
  }
}

/** True when there is anything worth opening the form with. */
export const hasPrefill = (search) => {
  const { pickup, dropoff } = readPrefill(search)

  return Boolean(pickup || dropoff)
}
